"use client";

import React, { useEffect, useRef, useState } from "react";
import { Workflow, Cpu, Database, TrendingUp, Settings, BarChart3, Users } from "lucide-react";
import { Badge } from "@/components/ui/badge";
type UseRevealOnScrollOptions = {
  once?: boolean;
  threshold?: number | number[];
  rootMargin?: string;
};
function useRevealOnScroll<T extends HTMLElement = HTMLElement>({
  once = true,
  threshold = 0.2,
  rootMargin = "0px"
}: UseRevealOnScrollOptions = {}) {
  const ref = useRef<T | null>(null);
  const [isVisible, setIsVisible] = useState(false);
  useEffect(() => {
    const node = ref.current;
    if (!node) {
      return;
    }
    if (typeof window === "undefined" || typeof IntersectionObserver === "undefined") {
      setIsVisible(true);
      return;
    }
    const observer = new IntersectionObserver(entries => {
      const entry = entries[0];
      if (entry?.isIntersecting) {
        setIsVisible(true);
        if (once) {
          observer.unobserve(entry.target);
        }
      } else if (!once) {
        setIsVisible(false);
      }
    }, {
      threshold,
      rootMargin
    });
    observer.observe(node);
    return () => {
      observer.disconnect();
    };
  }, [once, rootMargin, threshold]);
  return {
    ref,
    isVisible
  };
}
const modules = [{
  id: 1,
  title: "AI, Data & Intelligent Systems",
  description: "We architect high-performance data ecosystems and deploy custom LLMs that turn fragmented information into a strategic asset. Our AI-first approach ensures your enterprise moves with computational speed and precision.",
  iconName: "Database",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1604077/img/ai_data_systems.png"
}, {
  id: 2,
  title: "Enterprise Platforms & CRM",
  description: "Modernize your core stack with agile enterprise platforms designed for the digital age. We specialize in complex CRM migrations and ERP optimizations that unify your operational data and customer insights.",
  iconName: "Cpu",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1604077/img/enterprise_platforms.png"
}, {
  id: 3,
  title: "Intelligent Automation",
  description: "Eliminate operational friction through intelligent process automation. We design autonomous workflows that reduce cost, eliminate manual error, and allow your human capital to focus on high-leverage execution.",
  iconName: "Workflow",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1604077/img/intelligent_automation.png"
}, {
  id: 4,
  title: "Digital Engineering",
  description: "Build resilient, scalable software infrastructure that supports rapid growth. From legacy modernization to cloud-native development, our engineering team delivers production-ready systems that last.",
  iconName: "Settings",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1604077/img/digital_engineering.png"
}, {
  id: 5,
  title: "Integrated Cybersecurity",
  description: "Security is not a bolt-on; it's the foundation. Through our partnership with Cytrusst, we integrate GRC, ASM, and CSPM directly into your transformation roadmap to ensure total enterprise resilience.",
  iconName: "TrendingUp",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1604077/img/cybersecurity_cytrusst.png"
}, {
  id: 6,
  title: "Enterprise Business Planning",
  description: "Unify your strategic vision with operational execution through integrated business planning. We deploy advanced modeling frameworks that synchronize finance, supply chain, and sales for real-time decision making.",
  iconName: "BarChart3",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1604077/img/enterprise_business_planning.png"
}, {
  id: 7,
  title: "CPQ with CRM",
  description: "Optimize your quote-to-cash cycle with intelligent CPQ integrations. We bridge the gap between complex product configurations and CRM data to accelerate deal velocity and ensure pricing accuracy.",
  iconName: "Users",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1604077/img/cpq_crm_integration.png"
}];
const integrations = [{
  moduleId: 1,
  name: "OpenAI"
}, {
  moduleId: 1,
  name: "Anthropic"
}, {
  moduleId: 1,
  name: "Databricks"
}, {
  moduleId: 1,
  name: "Snowflake"
}, {
  moduleId: 2,
  name: "Salesforce"
}, {
  moduleId: 2,
  name: "HubSpot"
}, {
  moduleId: 2,
  name: "SAP"
}, {
  moduleId: 2,
  name: "Oracle"
}, {
  moduleId: 3,
  name: "Zapier"
}, {
  moduleId: 3,
  name: "Make"
}, {
  moduleId: 3,
  name: "n8n"
}, {
  moduleId: 3,
  name: "UIPath"
}, {
  moduleId: 4,
  name: "AWS"
}, {
  moduleId: 4,
  name: "Azure"
}, {
  moduleId: 4,
  name: "Docker"
}, {
  moduleId: 4,
  name: "Kubernetes"
}, {
  moduleId: 4,
  name: "Digital Transformation Consulting"
}, {
  moduleId: 4,
  name: "Cloud and iPaaS"
}, {
  moduleId: 4,
  name: "Enterprise Architecture"
}, {
  moduleId: 4,
  name: "Intelligent Test Automation"
}, {
  moduleId: 4,
  name: "NextGen UI & UX"
}, {
  moduleId: 5,
  name: "GRC"
}, {
  moduleId: 5,
  name: "ASM"
}, {
  moduleId: 5,
  name: "CSPM"
}, {
  moduleId: 5,
  name: "TPRM"
}, {
  moduleId: 6,
  name: "Financial Planning & Analysis"
}, {
  moduleId: 6,
  name: "Supply Chain Planning"
}, {
  moduleId: 6,
  name: "AI-led Sales Performance Planning"
}, {
  moduleId: 6,
  name: "Marketing Intellect Planning"
}, {
  moduleId: 6,
  name: "Workforce Planning"
}, {
  moduleId: 7,
  name: "Salesforce Implementation"
}, {
  moduleId: 7,
  name: "Sales/Service/Marketing/Commerce Cloud"
}, {
  moduleId: 7,
  name: "Einstein Analytics & Salesforce AI"
}, {
  moduleId: 7,
  name: "Einstein Bots and Automation"
}, {
  moduleId: 7,
  name: "CoE & Salesforce Health Checks"
}, {
  moduleId: 7,
  name: "DevOps Transformation"
}];
const iconMap: Record<string, React.ElementType> = {
  Workflow,
  Cpu,
  Database,
  TrendingUp,
  Settings,
  BarChart3,
  Users
};
function ModuleRow({
  module,
  moduleIntegrations,
  isEven,
  index
}: {
  module: (typeof modules)[0];
  moduleIntegrations: typeof integrations;
  isEven: boolean;
  index: number;
}) {
  const {
    ref,
    isVisible
  } = useRevealOnScroll({
    threshold: 0.15
  });
  const IconComponent = iconMap[module.iconName];
  return <div data-section-id="143854" data-index={index} className={`py-24 lg:py-32 ${isEven ? "bg-background" : "bg-slate-50/50"}`}>
      <div className="container mx-auto px-4 md:px-6">
        <div ref={ref} className={`flex flex-col gap-12 lg:gap-24 items-center transition-all duration-1000 ease-out ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-12"} ${isEven ? "lg:flex-row" : "lg:flex-row-reverse"}`}>
          <div className="w-full lg:w-1/2">
            <div className="relative aspect-[4/3] lg:aspect-square overflow-hidden rounded-lg border border-border/40 bg-muted/20 shadow-lg shadow-blue-900/5">
              <div className="absolute inset-0 bg-gradient-to-tr from-primary/5 to-transparent z-10 mix-blend-overlay" />
              <img src={module.image} alt={module.title} className="object-cover w-full h-full transition-transform duration-700 hover:scale-105" loading="lazy" />
            </div>
          </div>

          <div className="w-full lg:w-1/2 space-y-8">
            <div className="space-y-6">
              <div className="inline-flex items-center justify-center p-4 rounded-lg bg-blue-50 border border-blue-100 text-primary shadow-sm">
                {IconComponent && <IconComponent className="w-8 h-8" />}
              </div>
              
              <div className="space-y-4">
                <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold tracking-tight text-foreground">
                  {module.title}
                </h2>
                <p className="text-lg md:text-xl text-slate-500 leading-relaxed max-w-2xl">
                  {module.description}
                </p>
              </div>
            </div>

            <div className="space-y-4 pt-4 border-t border-border/60">
              <h4 className="text-xs font-bold tracking-widest text-slate-400 uppercase font-mono">
                Core Capabilities
              </h4>
              <div className="flex flex-wrap gap-2.5">
                {moduleIntegrations.map((integration, i) => <Badge key={i} data-index={i} variant="outline" className="px-3 py-1.5 text-sm border-slate-200 text-slate-600 bg-white/80 backdrop-blur-sm hover:bg-slate-100 hover:text-foreground transition-colors rounded-sm">
                    {integration.name}
                  </Badge>)}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>;
}
export default function OurCapabilities() {
  const {
    ref: headerRef,
    isVisible: isHeaderVisible
  } = useRevealOnScroll({
    threshold: 0.5
  });
  return <section id="service-portfolios" className="bg-background text-foreground w-full overflow-hidden">
      <div className="container mx-auto px-4 md:px-6 pt-24 lg:pt-32 pb-12">
        <div ref={headerRef} className={`max-w-3xl mx-auto text-center space-y-6 transition-all duration-1000 ${isHeaderVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
          <h2 className="text-xs font-bold tracking-widest text-primary uppercase font-mono">
            Sovereign Modernism
          </h2>
          <h3 className="text-4xl md:text-5xl lg:text-6xl font-extrabold tracking-tight text-foreground">
            Core Capabilities
          </h3>
          <p className="text-xl text-slate-500">
            Strategic AI-first transformation frameworks designed to modernize data, deploy intelligent systems, and drive measurable performance.
          </p>
        </div>
      </div>

      <div className="flex flex-col w-full">
        {modules.map((module, index) => {
        const isEven = index % 2 === 0;
        const moduleIntegrations = integrations.filter(i => i.moduleId === module.id);
        return <ModuleRow key={module.id} module={module} moduleIntegrations={moduleIntegrations} isEven={isEven} index={index} />;
      })}
      </div>
    </section>;
}